import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Quick task 260810-lpq (LPQ-03) — shared thumbnail + hover-preview popover
 * for image attachments, used on both the comment surface (AttachmentRow)
 * and the task-level surface (TaskAttachmentsSection).
 *
 * Renders a fixed 40px rounded thumbnail when `src` is present, or an
 * animated placeholder while the signed URL is still minting. Hovering (or
 * focusing, for keyboard users) reveals a larger preview popover. The popover
 * carries `pointer-events-none` so it never intercepts clicks meant for the
 * thumbnail/link beneath it, and is hidden again on mouse-leave / blur.
 *
 * Wrapped in an inline `<span>` (not a `<div>`) so this stays valid nested
 * inside an `<a>` — the image-attachment chip pattern in AttachmentRow.
 *
 * MKS-274 — the popover is portalled to `document.body` and positioned
 * `fixed` from the thumbnail's viewport rect, so no scroll container (the
 * task slide-over) can clip it, and the image is capped at 80vw/80vh instead
 * of 192px so a page screenshot is actually readable. `onOpen`, when given,
 * makes the thumbnail itself open the caller's full-size lightbox.
 */
export interface ImagePreviewThumbProps {
  src?: string;
  alt: string;
  testId?: string;
  /**
   * MKS-274: activate the caller's full-size lightbox from the thumbnail
   * itself. Omit it where the thumbnail already sits inside a clickable
   * element (the click bubbles there instead).
   */
  onOpen?: () => void;
}

/** Gap kept between the popover and both the thumbnail and the viewport edge. */
const PREVIEW_MARGIN = 8;

export function ImagePreviewThumb({ src, alt, testId, onOpen }: ImagePreviewThumbProps) {
  const [showPreview, setShowPreview] = useState(false);
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const popoverRef = useRef<HTMLSpanElement | null>(null);
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null);

  const isOpen = showPreview && !!src;

  // Place the portalled popover above the thumbnail, flipping below when it
  // does not fit there, and clamping both axes so it is never cut off by a
  // window edge. Called after mount, after the image loads (its natural size
  // is unknown until then) and on scroll/resize while open.
  const reposition = useCallback(() => {
    const anchor = anchorRef.current?.getBoundingClientRect();
    const popover = popoverRef.current?.getBoundingClientRect();
    if (!anchor || !popover) return;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = anchor.top - PREVIEW_MARGIN - popover.height;
    if (top < PREVIEW_MARGIN) {
      const below = anchor.bottom + PREVIEW_MARGIN;
      top =
        below + popover.height <= viewportHeight - PREVIEW_MARGIN
          ? below
          : Math.max(PREVIEW_MARGIN, viewportHeight - PREVIEW_MARGIN - popover.height);
    }

    const centered = anchor.left + anchor.width / 2 - popover.width / 2;
    const maxLeft = Math.max(PREVIEW_MARGIN, viewportWidth - PREVIEW_MARGIN - popover.width);
    const left = Math.min(Math.max(PREVIEW_MARGIN, centered), maxLeft);

    setCoords({ left, top });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    reposition();
    // Capture phase so the slide-over's own scroll container is heard too.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen, reposition]);

  const clickable = !!onOpen;

  function handleOpen() {
    setShowPreview(false);
    onOpen?.();
  }

  return (
    <span
      ref={anchorRef}
      className={`relative inline-flex${clickable ? ' cursor-zoom-in' : ''}`}
      tabIndex={0}
      role={clickable ? 'button' : undefined}
      aria-label={clickable ? `Open ${alt} full size` : undefined}
      onMouseEnter={() => setShowPreview(true)}
      onMouseLeave={() => setShowPreview(false)}
      onFocus={() => setShowPreview(true)}
      onBlur={() => setShowPreview(false)}
      onClick={
        clickable
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              handleOpen();
            }
          : undefined
      }
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              handleOpen();
            }
          : undefined
      }
      data-testid={testId ? `${testId}-wrapper` : undefined}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="w-10 h-10 object-cover rounded"
          data-testid={testId}
        />
      ) : (
        <span
          className="w-10 h-10 rounded bg-gray-100 animate-pulse"
          aria-label="Loading thumbnail"
          data-testid={testId ? `${testId}-placeholder` : undefined}
        />
      )}
      {isOpen &&
        createPortal(
          <span
            ref={popoverRef}
            className="pointer-events-none fixed z-[80] rounded-lg border border-gray-200 bg-white p-1 shadow-2xl"
            style={{
              left: coords?.left ?? 0,
              top: coords?.top ?? 0,
              // Hidden (but laid out, so it can be measured) until placed, so
              // the preview never flashes at the wrong position.
              visibility: coords ? 'visible' : 'hidden',
            }}
            data-testid={testId ? `${testId}-preview` : 'image-preview-popover'}
          >
            <img
              src={src}
              alt={alt}
              onLoad={reposition}
              className="max-h-[80vh] max-w-[80vw] rounded object-contain"
            />
          </span>,
          document.body,
        )}
    </span>
  );
}
